// @flow
import React, {useEffect, useState} from 'react';
import {
  makeStyles,
  Typography,
  Grid,
  InputAdornment,
  Divider,
} from '@material-ui/core';
import {Controller, useForm} from 'react-hook-form';
import {useParams, useHistory} from 'react-router-dom';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import {colors} from '../../../../../assets/styles/colors';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {
  useCRUDStandardRate,
  useDataShippingEstandar,
  useFetchShippingMethods,
  useFetchShippingStandard,
  useGetCurrencyCountry,
  useGetShippingMethodActive,
  useResetState,
  useUpdateShippingMethodActive,
} from '../utils';
import {useStoreCountry} from '../../../../country/utils';
import ActivateShippingModal from '../components/ActivateShippingModal';
import type {Country} from '../../../../../entities';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    borderLeft: `1px solid #dbdbe0`,
    borderRight: `1px solid #dbdbe0`,
    borderBottom: `1px solid #dbdbe0`,
  },
  contentBackground: {
    backgroundColor: '#f8f9fb',
    padding: theme.spacing(2),
    borderRadius: '12px',
    marginTop: theme.spacing(2),
  },
  greyText: {
    color: colors.grey600,
    fontSize: '0.75rem',
  },
  messageSpace: {
    marginBottom: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  messageField: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  messageTips: {
    margin: theme.spacing(0, 3),
    marginInline: 0,
    [theme.breakpoints.down('md')]: {
      margin: 'initial',
    },
  },
  messageRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(6),
  },
  bold: {
    fontWeight: 600,
  },
  saveButton: {
    marginTop: theme.spacing(2),
    width: 'fit-content',
    paddingInline: theme.spacing(8),
  },
  form: {
    paddingInline: theme.spacing(2),
    paddingBottom: '22px',
  },
  select: {
    backgroundColor: 'white',
  },
}));

export default function LogisticShippingInternational() {
  const classes = useStyles();
  const history = useHistory();
  const {adminRateId} = useParams();

  const [showModalActive, setShowModalActive] = useState(false);
  const [optionSave, setOptionSave] = useState('');
  const {
    register,
    errors,
    control,
    getValues,
    handleSubmit,
    formState,
  } = useForm();
  const {isDirty} = formState;
  const country = useStoreCountry();
  const currency = useGetCurrencyCountry();
  const {shippingDataEstandar} = useDataShippingEstandar();
  const {updateShippingMethodActive} = useUpdateShippingMethodActive();
  const {availableShippingCountries} = useFetchShippingMethods();
  const {
    shippingStandard,
    fetchShippingStandardData,
  } = useFetchShippingStandard();
  const {
    isSaving,
    savingStandardRateSuccess,
    createStandardRate,
    updateStandardRate,
  } = useCRUDStandardRate();
  const shippingMethod = useGetShippingMethodActive();
  const {resetState} = useResetState();

  const existingCountryCodes =
    shippingDataEstandar && shippingDataEstandar.map(data => data.country.code);

  const countriesAvailableToAdd = availableShippingCountries.filter(
    country => !existingCountryCodes.includes(country.code)
  );

  const handleValidation = () => {
    if (
      shippingMethod.shippingMethodActive.storeShippingMethodSettings
        .storeShippingMethodType !== 'STANDARD'
    ) {
      setShowModalActive(true);
      return false;
    }
    return true;
  };

  const handleBeforeSubmit = () => {
    if (!handleValidation()) {
      return;
    }

    handleFormSubmit('save');
  };

  const handleFormSubmit = (optionSave: string) => {
    const data = getValues();

    const formattedRateRequest = {
      ...data,
      enabled: true,
      storeShippingMethodSubType: 'BASIC',
    };

    if (adminRateId) {
      updateStandardRate({
        standardRateId: adminRateId,
        standardRate: formattedRateRequest,
      });
    } else {
      createStandardRate(formattedRateRequest);
    }

    if (optionSave === 'saveAndActivate') {
      setOptionSave('saveAndActivate');
    }
  };

  useEffect(() => {
    if (savingStandardRateSuccess) {
      if (optionSave === 'saveAndActivate') {
        updateShippingMethodActive({
          shippingMethodType: 'STANDARD',
        });
      }
      history.push('/configure/logistics/shipping/standard');
    }
  }, [
    savingStandardRateSuccess,
    history,
    updateShippingMethodActive,
    optionSave,
  ]);

  useEffect(() => {
    if (adminRateId) {
      fetchShippingStandardData(adminRateId);
    }
  }, [adminRateId, fetchShippingStandardData]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <CustomExitPrompt when={isDirty && !savingStandardRateSuccess} />
      <Divider />
      {!adminRateId && (
        <form
          onSubmit={handleSubmit(handleBeforeSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <Controller
                  name="country"
                  control={control}
                  rules={{required: 'Campo obligatorio'}}
                  defaultValue={''}
                  as={
                    <CustomSelect
                      native
                      fullWidth
                      className={classes.select}
                      label={'Seleccionar País'}
                      error={!!errors.country}
                      helperText={errors.country ? errors.country.message : ''}
                      disabled={isSaving}
                    >
                      <option value={'none'} hidden />
                      {countriesAvailableToAdd
                        .filter(c => c.code !== country.code)
                        .map((c: Country, index) => (
                          <option key={`country-${c.name}`} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                    </CustomSelect>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío aproximado"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Mensaje"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
                <div className={classes.messageTips}>
                  <Typography className={classes.greyText} paragraph>
                    Es importante incluir información como:
                  </Typography>
                  <div className={classes.messageRow}>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Días de despacho
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Despachos los martes y jueves
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Rango de tiempo hasta la recepción del pedido
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Argentina: entre 24 a 48 horas | Brazil: 3 - 4 días
                        útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>

          <CustomButton
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={isSaving}
          >
            Guardar
          </CustomButton>
        </form>
      )}

      {adminRateId && shippingStandard && (
        <form
          onSubmit={handleSubmit(handleBeforeSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <Controller
                  name="country"
                  control={control}
                  rules={{required: 'Campo obligatorio'}}
                  defaultValue={
                    shippingStandard ? shippingStandard.country.code : ''
                  }
                  as={
                    <CustomSelect
                      native
                      fullWidth
                      className={classes.select}
                      label={'Seleccionar País'}
                      error={!!errors.country}
                      helperText={errors.country ? errors.country.message : ''}
                      disabled={true}
                    >
                      <option value={'none'} hidden />
                      {availableShippingCountries
                        .filter(c => c.code !== country.code)
                        .map((c: Country, index) => (
                          <option key={`country-${c.name}`} value={c.code}>
                            {c.name}
                          </option>
                        ))}
                    </CustomSelect>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  defaultValue={
                    shippingStandard ? shippingStandard.displayName : ''
                  }
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío aproximado"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  defaultValue={shippingStandard ? shippingStandard.price : ''}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Mensaje"
                    defaultValue={
                      shippingStandard ? shippingStandard.description : ''
                    }
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
                <div className={classes.messageTips}>
                  <Typography className={classes.greyText} paragraph>
                    Es importante incluir información como:
                  </Typography>
                  <div className={classes.messageRow}>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Días de despacho
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Despachos los martes y jueves
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Rango de tiempo hasta la recepción del pedido
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Argentina: entre 24 a 48 horas | Brazil: 3 - 4 días
                        útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>

          <CustomButton
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={isSaving}
          >
            Guardar
          </CustomButton>
        </form>
      )}

      <ActivateShippingModal
        open={showModalActive}
        handleClose={() => {
          setShowModalActive(false);
        }}
        typeRateSelected="STANDARD"
        handleFormSubmit={handleFormSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
